/**
 * JITAccessRequestPanel - JIT Access Request Form
 *
 * FERP CRITICAL: Request temporary role elevation with approval workflow
 *
 * Features:
 * - Role selection from policy-allowed roles
 * - Duration selection (policy-enforced limits)
 * - Justification requirement
 * - Emergency access flag
 * - Real-time policy validation
 * - Auto-approval indication
 * - Complete audit trail
 *
 * Architecture Principles:
 * - Data-driven (all roles/policies from backend)
 * - Repository pattern (JITAccessRepository)
 * - Policy-enforced validation
 * - Immutable audit trails
 *
 * GitHub Issue: #343
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Stack,
  Title,
  Text,
  Select,
  Textarea,
  Button,
  Alert,
  Group,
  Card,
  NumberInput,
  Switch,
  Badge,
  Loader,
  Box,
  List,
} from '@mantine/core';
import {
  IconClock,
  IconShield,
  IconAlertCircle,
  IconCheck,
  IconSend,
} from '@tabler/icons-react';
import { useApolloClient } from '@apollo/client/react';
import { ApolloClient } from '@apollo/client';
import {
  JITAccessRepository,
  RequestJITAccessInput,
  JITPolicy,
  RequestJITAccessResult,
} from '../data/repositories/JITAccessRepository';
import { RoleRepository, RoleDefinition } from '../data/repositories/RoleRepository';

export interface JITAccessRequestPanelProps {
  /** Tenant ID for multi-tenant isolation */
  tenantId: string;
  /** Current user ID */
  userId: string;
  /** Current user email */
  userEmail: string;
  /** Current user role */
  currentRole: string;
  /** Callback when request is submitted */
  onRequestSubmit?: (result: RequestJITAccessResult) => void;
  /** Callback on error */
  onError?: (error: Error) => void;
}

/**
 * JIT Access Request Panel
 *
 * FERP Architecture Compliance:
 * - ✅ Data-driven (roles/policies from backend)
 * - ✅ Repository pattern (JITAccessRepository)
 * - ✅ Policy-enforced validation
 * - ✅ Immutable audit trails
 */
export function JITAccessRequestPanel({
  tenantId,
  userId,
  userEmail,
  currentRole,
  onRequestSubmit,
  onError,
}: JITAccessRequestPanelProps) {
  const apolloClient = useApolloClient() as ApolloClient;
  const jitRepository = useMemo(() => new JITAccessRepository(apolloClient), [apolloClient]);
  const roleRepository = useMemo(() => new RoleRepository(apolloClient), [apolloClient]);

  // State
  const [policy, setPolicy] = useState<JITPolicy | null>(null);
  const [availableRoles, setAvailableRoles] = useState<RoleDefinition[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<RequestJITAccessResult | null>(null);

  // Form state
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [duration, setDuration] = useState<number>(60); // Default 1 hour
  const [justification, setJustification] = useState<string>('');
  const [emergency, setEmergency] = useState<boolean>(false);

  /**
   * Load JIT policy and available roles
   */
  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        setError(null);

        // Load JIT policy
        const policyData = await jitRepository.getPolicy(tenantId);
        if (!policyData) {
          throw new Error('JIT access policy not configured for this tenant');
        }
        if (!policyData.enabled) {
          throw new Error('JIT access is currently disabled for this tenant');
        }
        setPolicy(policyData);

        // Load available roles (only those allowed by policy)
        const allRoles = await roleRepository.listRoles(tenantId);
        const allowedRoles = allRoles.filter(role =>
          policyData.allowedRoles.includes(role.roleName)
        );
        setAvailableRoles(allowedRoles);

        // Set default duration from policy
        setDuration(policyData.defaultMaxDurationMinutes);
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Failed to load JIT access configuration';
        setError(errorMsg);
        if (onError) {
          onError(err instanceof Error ? err : new Error(errorMsg));
        }
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [tenantId, jitRepository, roleRepository, onError]);

  /**
   * Calculate if request will be auto-approved
   */
  const willAutoApprove = useMemo(() => {
    if (!policy || !selectedRole) return false;
    if (emergency) return true; // Emergency access always auto-approved
    return (
      policy.autoApprovalEnabled &&
      policy.autoApprovalRoles.includes(selectedRole) &&
      duration <= policy.autoApprovalMaxDurationMinutes
    );
  }, [policy, selectedRole, duration, emergency]);

  /**
   * Get maximum allowed duration for selected role
   */
  const maxDuration = useMemo(() => {
    if (!policy) return 480; // Default 8 hours
    return policy.defaultMaxDurationMinutes;
  }, [policy]);

  /**
   * Validate form
   */
  const isFormValid = useMemo(() => {
    return (
      selectedRole !== '' &&
      duration > 0 &&
      duration <= maxDuration &&
      justification.trim().length >= 20 // Minimum 20 characters
    );
  }, [selectedRole, duration, maxDuration, justification]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(async () => {
    if (!isFormValid) return;

    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);

      const input: RequestJITAccessInput = {
        tenantId,
        requesterId: userId,
        requesterEmail: userEmail,
        requesterRole: currentRole,
        requestedRole: selectedRole,
        durationMinutes: duration,
        justification: justification.trim(),
        emergency,
      };

      const result = await jitRepository.requestAccess(input);
      setSuccess(result);

      // Reset form on success
      setSelectedRole('');
      setDuration(policy?.defaultMaxDurationMinutes || 60);
      setJustification('');
      setEmergency(false);

      if (onRequestSubmit) {
        onRequestSubmit(result);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to submit request';
      setError(errorMsg);
      if (onError) {
        onError(err instanceof Error ? err : new Error(errorMsg));
      }
    } finally {
      setSubmitting(false);
    }
  }, [
    isFormValid,
    tenantId,
    userId,
    userEmail,
    currentRole,
    selectedRole,
    duration,
    justification,
    emergency,
    policy,
    jitRepository,
    onRequestSubmit,
    onError,
  ]);

  // Loading state
  if (loading) {
    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="center" py="xl">
          <Loader size="lg" />
          <Text size="sm" c="dimmed">Loading JIT access configuration...</Text>
        </Group>
      </Card>
    );
  }

  // Error state (no policy or disabled)
  if (error && !policy) {
    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Alert icon={<IconAlertCircle size={16} />} title="Configuration Error" color="red">
          {error}
        </Alert>
      </Card>
    );
  }

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={3}>Request Temporary Access</Title>
            <Text size="sm" c="dimmed">
              Request elevated role access for a limited time period
            </Text>
          </div>
          <Badge
            size="lg"
            color={policy?.enabled ? 'green' : 'red'}
            variant="light"
            leftSection={<IconShield size={14} />}
          >
            {policy?.enabled ? 'Enabled' : 'Disabled'}
          </Badge>
        </Group>

        {/* Success message */}
        {success && (
          <Alert icon={<IconCheck size={16} />} title="Request Submitted" color="green">
            <Stack gap="xs">
              <Text size="sm">
                {success.message}
              </Text>
              <Group gap="xs">
                <Text size="sm" fw={500}>Request ID:</Text>
                <Text size="sm" ff="monospace">{success.requestId}</Text>
              </Group>
              {success.requiresApproval ? (
                <Text size="sm" c="dimmed">
                  Your request has been submitted for approval. You will be notified when it is processed.
                </Text>
              ) : (
                <Text size="sm" c="dimmed">
                  Your access has been automatically granted and is now active.
                </Text>
              )}
            </Stack>
          </Alert>
        )}

        {/* Error message */}
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
            {error}
          </Alert>
        )}

        {/* Form */}
        <Stack gap="sm">
          {/* Role selection */}
          <Select
            label="Requested Role"
            placeholder="Select a role to request access to"
            description="Only roles allowed by your tenant policy are shown"
            data={availableRoles.map(role => ({
              value: role.roleName,
              label: `${role.displayName} (${role.roleName})`,
            }))}
            value={selectedRole}
            onChange={(value: string | null) => setSelectedRole(value || '')}
            required
            disabled={submitting}
            searchable
            clearable
          />

          {/* Duration input */}
          <NumberInput
            label="Duration (minutes)"
            placeholder="Enter duration in minutes"
            description={`Maximum allowed: ${maxDuration} minutes (${Math.floor(maxDuration / 60)} hours)`}
            value={duration}
            onChange={(value: number | string) => setDuration(typeof value === 'number' ? value : 60)}
            min={15}
            max={maxDuration}
            step={15}
            required
            disabled={submitting}
            leftSection={<IconClock size={16} />}
          />

          {/* Justification */}
          <Textarea
            label="Justification"
            placeholder="Explain why you need this access (minimum 20 characters)"
            description="Provide a clear business justification for this access request"
            value={justification}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setJustification(e.currentTarget.value)}
            minRows={3}
            maxRows={6}
            required
            disabled={submitting}
            error={
              justification.length > 0 && justification.trim().length < 20
                ? `Minimum 20 characters required (current: ${justification.trim().length})`
                : undefined
            }
          />

          {/* Emergency flag */}
          {policy?.emergencyAccessEnabled && (
            <Switch
              label="Emergency Access"
              description="Check this if you need immediate access for an emergency situation (automatically approved)"
              checked={emergency}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmergency(e.currentTarget.checked)}
              disabled={submitting}
              color="red"
            />
          )}

          {/* Auto-approval indicator */}
          {willAutoApprove && (
            <Alert icon={<IconCheck size={16} />} color="blue" variant="light">
              <Stack gap="xs">
                <Text size="sm" fw={500}>Auto-Approval Eligible</Text>
                <Text size="sm">
                  {emergency
                    ? 'Emergency requests are automatically approved and access is granted immediately.'
                    : 'This request meets the criteria for automatic approval. Access will be granted immediately upon submission.'}
                </Text>
              </Stack>
            </Alert>
          )}

          {/* Policy information */}
          {policy && (
            <Alert icon={<IconAlertCircle size={16} />} color="gray" variant="light">
              <Stack gap="xs">
                <Text size="sm" fw={500}>Policy Information</Text>
                <List fz="sm" spacing="xs">
                  <List.Item>
                    Maximum duration: {maxDuration} minutes ({Math.floor(maxDuration / 60)} hours)
                  </List.Item>
                  {policy.extensionEnabled && (
                    <List.Item>
                      Session extensions: Enabled (max {policy.maxExtensionCount} extensions)
                    </List.Item>
                  )}
                  {policy.autoApprovalEnabled && (
                    <List.Item>
                      Auto-approval: Enabled for durations ≤ {policy.autoApprovalMaxDurationMinutes} minutes
                    </List.Item>
                  )}
                </List>
              </Stack>
            </Alert>
          )}

          {/* Submit button */}
          <Button
            fullWidth
            size="md"
            onClick={handleSubmit}
            disabled={!isFormValid || submitting}
            loading={submitting}
            leftSection={<IconSend size={16} />}
          >
            {submitting ? 'Submitting Request...' : 'Submit Request'}
          </Button>
        </Stack>
      </Stack>
    </Card>
  );
}
